import { Box, IconButton, Tooltip } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { NAVIGATION_KEYS, NS } from '../../../i18n/constants/keys';
import {
  BriefcaseBusinessIcon,
  ChartPieIcon,
  CircleHelpIcon,
  FileTextIcon,
  LayoutGridIcon,
  SettingsIcon,
  UserRoundCheckIcon,
  UsersRoundIcon,
} from '../../../icons/animated';
import { Permission } from '../../../utils/helpers/permissions';
import { RootState } from '../../../utils/redux_store/store';
import { AppsDockTrigger } from '../AppsFolder';

interface INavBarShortcutsProps {
  isJobPosting: boolean;
  isAppsOpen: boolean;
  onOpenApps: () => void;
}

const SYSTEM_SETTINGS_PATHS = [
  '/system-settings',
  '/access-management',
  '/assessment-framework',
  '/organization-settings',
  '/employee-management',
  '/candidate-management',
  '/partner-management',
];

export const NavBarShortcuts = ({
  isJobPosting,
  isAppsOpen,
  onOpenApps,
}: INavBarShortcutsProps) => {
  const { t: tNavigation } = useTranslation(NS.navigation);
  const location = useLocation();
  const permissions = useSelector((state: RootState) => state.permissions.permissions);
  const { subscriptions } = useSelector((state: RootState) => state.subscriptions);
  const role = localStorage.getItem('role');

  const shortcuts = [
    {
      id: 'dashboard',
      label: tNavigation(NAVIGATION_KEYS.sidebar.dashboard),
      to: '/home',
      icon: <LayoutGridIcon color="#ffffff" size={22} />,
      background: 'linear-gradient(135deg, #10b981, #0f766e)',
    },
    {
      id: 'hierTalent',
      label: tNavigation(NAVIGATION_KEYS.sidebar.hierTalent),
      to: '/job-openings-list',
      icon: <BriefcaseBusinessIcon color="#ffffff" size={22} />,
      background: 'linear-gradient(135deg, #3b82f6, #4338ca)',
      requiredPermissions: [Permission.ASSESSMENTS_CREATE, Permission.QUESTIONS_VIEW],
      matchAnyPermission: true,
    },
    {
      id: 'questions',
      label: tNavigation(NAVIGATION_KEYS.sidebar.questions),
      to: '/question-bank',
      icon: <CircleHelpIcon color="#ffffff" size={22} />,
      background: 'linear-gradient(135deg, #c026d3, #e11d48)',
      requiredPermissions: [Permission.QUESTIONS_VIEW],
    },
    {
      id: 'reporting',
      label: tNavigation(NAVIGATION_KEYS.sidebar.reporting),
      to: '/reporting',
      icon: <ChartPieIcon color="#ffffff" size={22} />,
      background: 'linear-gradient(135deg, #f59e0b, #ea580c)',
    },
    {
      id: 'jobPostings',
      label: tNavigation(NAVIGATION_KEYS.sidebar.jobPostings),
      to: '/jobs',
      icon: <FileTextIcon color="#ffffff" size={22} />,
      background: 'linear-gradient(135deg, #06b6d4, #2563eb)',
    },
    {
      id: 'candibank',
      label: tNavigation(NAVIGATION_KEYS.sidebar.candibankExplorer),
      to: '/candibank-explorer',
      icon: <UsersRoundIcon color="#ffffff" size={22} />,
      background: 'linear-gradient(135deg, #14b8a6, #0369a1)',
      hidden: !subscriptions?.hierTalentCandiBank || role === 'Candidate',
    },
    {
      id: 'settings',
      label: 'System Settings',
      to: '/system-settings',
      icon: <SettingsIcon color="#ffffff" size={22} />,
      background: 'linear-gradient(135deg, #64748b, #1e293b)',
      hidden: role === 'Candidate',
    },
    {
      id: 'profile',
      label: 'Profile & Account',
      to: '/user-profile',
      icon: <UserRoundCheckIcon color="#ffffff" size={22} />,
      background: 'linear-gradient(135deg, #0d9488, #2563eb)',
    },
  ];

  const visibleShortcuts = shortcuts.filter((shortcut) => {
    if (shortcut.hidden) return false;
    if (!shortcut.requiredPermissions) return true;
    return shortcut.matchAnyPermission
      ? shortcut.requiredPermissions.some((permission) => permissions.includes(permission))
      : shortcut.requiredPermissions.every((permission) => permissions.includes(permission));
  });

  if (isJobPosting) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, minWidth: 0 }}>
      <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
        <AppsDockTrigger onClick={onOpenApps} isOpen={isAppsOpen} />
      </Box>
      <Box
        component='nav'
        aria-label='Application shortcuts'
        sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}
      >
        {visibleShortcuts.map((shortcut) => {
          const isActive =
            shortcut.id === 'settings'
              ? SYSTEM_SETTINGS_PATHS.some(
                  (path) =>
                    location.pathname === path ||
                    location.pathname.startsWith(`${path}/`),
                )
              : location.pathname === shortcut.to ||
                location.pathname.startsWith(`${shortcut.to}/`);

          return (
            <Tooltip key={shortcut.id} title={shortcut.label} arrow>
              <IconButton
                component={Link}
                to={shortcut.to}
                aria-label={shortcut.label}
                aria-current={isActive ? 'page' : undefined}
                size='small'
                sx={{
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                  borderRadius: '8px',
                  background: shortcut.background,
                  boxShadow: isActive
                    ? '0 0 0 2px rgba(255,255,255,0.8)'
                    : '0 1px 3px rgba(0,0,0,0.18)',
                  transition: 'transform 160ms ease, box-shadow 160ms ease',
                  '& svg': { width: 20, height: 20 },
                  '&:hover': { background: shortcut.background, transform: 'translateY(-1px)' },
                  '&:focus-visible': { outline: '2px solid #fff', outlineOffset: 2 },
                }}
              >
                {shortcut.icon}
              </IconButton>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};
